import React, { useState, useMemo } from "react";
import { Modal, Button, Form, Badge, Spinner } from "react-bootstrap";
import moment from "moment";
import { API_URL } from "../..";

const ViewOvertimeModal = ({ overtime, onClose, onStatusChange }) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(overtime?.status || "pending");
  const [paymentType, setPaymentType] = useState("hourly"); // "hourly" or "fixed"
  const [value, setValue] = useState(""); // rate or fixed amount

  // Calculate overtime hours
  const totalHours = useMemo(() => {
    if (!overtime?.start_time || !overtime?.end_time) {
      return { formatted: "0h 0m", decimal: 0 };
    }

    const start = moment(overtime.start_time, "HH:mm:ss");
    const end = moment(overtime.end_time, "HH:mm:ss");
    let diffMinutes = end.diff(start, "minutes");
    if (diffMinutes < 0) diffMinutes += 24 * 60;

    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    const decimal = parseFloat((diffMinutes / 60).toFixed(2));

    return { formatted: `${hours}h ${minutes}m`, decimal };
  }, [overtime?.start_time, overtime?.end_time]);

  // Calculate total amount based on type
  const totalAmount = paymentType === "fixed"
    ? parseFloat(value) || 0
    : (parseFloat(value) || 0) * totalHours.decimal;

  if (!overtime) return null;

  const attachments = typeof overtime.attachments === "string"
    ? JSON.parse(overtime.attachments)
    : overtime.attachments || [];

  const handleAction = async (newStatus) => {
    if (newStatus === "approved") {
      if (!value || parseFloat(value) <= 0) {
        alert("Please enter a valid rate or fixed amount.");
        return;
      }
    }

    setLoading(true);

    try {
      const loggedUser = JSON.parse(localStorage.getItem("user"));
      const payload = {
        ids: [overtime.id],
        status: newStatus,
        approved_id: loggedUser?.id,
      };

      if (newStatus === "approved") {
        payload.type = paymentType; // "hourly" or "fixed"
        payload.value = parseFloat(value);
      }

      const res = await fetch(`${API_URL}/api/overtime/bulk/approve`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const json = await res.json();

      if (json.success) {
        setStatus(newStatus);
        if (onStatusChange) {
          onStatusChange(overtime.id, newStatus);
        }
        alert(`Overtime ${newStatus} successfully!`);
      } else {
        alert(json.message || "Action failed.");
      }
    } catch (err) {
      console.error("Overtime approval error:", err);
      alert("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Overtime Request — <span className="text-primary">{overtime.user?.name}</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="row mb-4">
          <div className="col-md-6">
            <p><strong>Date:</strong> {moment(overtime.date).format("DD MMM YYYY")}</p>
            <p><strong>Start:</strong> {moment(overtime.start_time, "HH:mm:ss").format("hh:mm A")}</p>
            <p><strong>End:</strong> {moment(overtime.end_time, "HH:mm:ss").format("hh:mm A")}</p>
            <p><strong>Duration:</strong> <strong className="text-primary">{totalHours.formatted}</strong> ({totalHours.decimal} hours)</p>
            <p>
              <strong>Status:</strong>{" "}
              <Badge bg={status === "approved" ? "success" : status === "rejected" ? "danger" : "warning"}>
                {status.toUpperCase()}
              </Badge>
            </p>
          </div>
          <div className="col-md-6">
            {overtime.description && (
              <>
                <strong>Description:</strong>
                <p className="text-muted">{overtime.description}</p>
              </>
            )}
          </div>
        </div>

        {/* Payment Section - Only for pending or approved */}
        {(status === "pending" || status === "approved") && (
          <div className="border rounded p-10 bg-light">
            <h6 className="mb-3"><strong>Overtime Payment</strong></h6>

            {status === "pending" ? (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Payment Type</Form.Label>
                  <div className="d-flex gap-4">
                    <Form.Check
                      type="radio"
                      label="Hourly Rate"
                      name="paymentType"
                      checked={paymentType === "hourly"}
                      onChange={() => setPaymentType("hourly")}
                    />
                    <Form.Check
                      type="radio"
                      label="Fixed Amount"
                      name="paymentType"
                      checked={paymentType === "fixed"}
                      onChange={() => setPaymentType("fixed")}
                    />
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    {paymentType === "fixed" ? "Fixed Amount (£)" : "Hourly Rate (£)"}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={paymentType === "fixed" ? "e.g. 150.00" : "e.g. 35.00"}
                    style={{ width: "200px" }}
                  />
                  {paymentType === "hourly" && (
                    <small className="text-muted">
                      × {totalHours.decimal.toFixed(2)} hours = <strong>£{totalAmount.toFixed(2)}</strong>
                    </small>
                  )}
                </Form.Group>
              </>
            ) : (
              <div className="text-success">
                <strong>
                  {overtime.fixed_overtime_amount
                    ? `Fixed Amount: £${parseFloat(overtime.fixed_overtime_amount).toFixed(2)}`
                    : `Hourly Rate: £${parseFloat(overtime.overtime_rate_per_hour || 0).toFixed(2)}/hr → £${totalAmount.toFixed(2)}`
                  }
                </strong>
              </div>
            )}

            <hr />

            <div className="d-flex justify-content-between align-items-center">
              <strong>Total Payable Amount:</strong>
              <span className="text-success fs-4">£{totalAmount.toFixed(2)}</span>
            </div>
          </div>
        )}

        {/* Attachments */}
        {attachments.length > 0 && (
          <>
            <hr className="my-4" />
            <h6>Attachments</h6>
            <div className="d-flex flex-wrap gap-3">
              {attachments.map((file, i) => (
                <a key={i} href={`${API_URL}${file}`} target="_blank" rel="noopener noreferrer">
                  <img
                    src={`${API_URL}${file}`}
                    alt="Attachment"
                    style={{
                      width: "120px",
                      height: "100px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      border: "2px solid #ddd",
                    }}
                  />
                </a>
              ))}
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          Close
        </Button>

        {status === "pending" && (
          <>
            <Button
              variant="success"
              disabled={loading || !value || parseFloat(value) <= 0}
              onClick={() => handleAction("approved")}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Approving...
                </>
              ) : (
                "Approve & Pay"
              )}
            </Button>

            <Button
              variant="danger"
              disabled={loading}
              onClick={() => handleAction("rejected")}
            >
              {loading ? "Rejecting..." : "Reject"}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ViewOvertimeModal;